import { collection, getDocs, addDoc } from 'firebase/firestore';

/** Campus code whose Courses rows are copied when adding a new campus */
export const SUBJECT_TEMPLATE_CAMPUS_CODE = 'QAD_RIQC1_2025_26';

/**
 * Clone Courses rows from source campus to target: same name, instructor, totalClasses, instructorId; campusCodes = [target].
 * Skips subject names that already exist for the target campus. Deduplicates by name on the template side.
 */
export async function copySubjectCoursesFromCampus(db, sourceCampusCode, targetCampusCode) {
    if (!sourceCampusCode || !targetCampusCode || sourceCampusCode === targetCampusCode) {
        return { created: 0, skippedExisting: 0, templateSubjects: 0 };
    }

    const coursesSnapshot = await getDocs(collection(db, 'Courses'));
    let maxId = 0;
    const existingNamesOnTarget = new Set();
    const rawFromSource = [];

    coursesSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const campusCodes = data.campusCodes || [];
        const courseId = parseInt(String(data.id), 10) || 0;
        if (courseId > maxId) maxId = courseId;

        if (campusCodes.includes(targetCampusCode) && data.name) {
            existingNamesOnTarget.add(String(data.name).trim());
        }

        if (campusCodes.includes(sourceCampusCode)) {
            rawFromSource.push({
                name: data.name,
                instructor: data.instructor,
                totalClasses: data.totalClasses,
                instructorId: data.instructorId
            });
        }
    });

    const seen = new Set();
    const templateSubjects = [];
    for (const row of rawFromSource) {
        const key = (row.name || '').trim();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        templateSubjects.push(row);
    }

    const toCreate = templateSubjects.filter((c) => !existingNamesOnTarget.has((c.name || '').trim()));

    let created = 0;
    for (let i = 0; i < toCreate.length; i++) {
        const course = toCreate[i];
        const newId = String(maxId + i + 1);
        const totalClasses = Number.isFinite(Number(course.totalClasses))
            ? parseInt(String(course.totalClasses), 10)
            : 0;
        const payload = {
            id: newId,
            name: (course.name || '').trim(),
            instructor: (course.instructor || '').trim(),
            totalClasses: Number.isNaN(totalClasses) ? 0 : totalClasses,
            campusCodes: [targetCampusCode]
        };
        if (course.instructorId) {
            payload.instructorId = course.instructorId;
        }
        await addDoc(collection(db, 'Courses'), payload);
        created++;
    }

    return {
        created,
        skippedExisting: templateSubjects.length - toCreate.length,
        templateSubjects: templateSubjects.length
    };
}
